import { Option, Schema } from "effect";
import { ChangeSetJson, CollabVersion, DraftId, EditorIdentity } from "./collab.ts";
import {
  CurriculumPhase,
  CurriculumRole,
  CurriculumStatus,
  CurriculumTags,
} from "./curriculum-tags.ts";
import { FindingSeverity } from "./finding-summary.ts";
import {
  AnnotationId,
  DocumentId,
  EditProposalId,
  RuleContentHash,
  RuleId,
  SuggestionId,
  ViewerId,
} from "./ids.ts";
import type { LineCounts } from "./line-counts.ts";
import { AnnotationScope, Disposition, EditOperation } from "./review.ts";

// The append-only audit-event union (R10). Every op the server performs appends one
// event; each mutating event carries enough to describe its inverse (R11, undo
// deferred). `at` is the server-assigned timestamp.

// A monotonic per-document version assigned by the commit seam (ADR-0006, set in P2.U2).
// Absent means the document has no committed patch chain yet.
export const DocVersion = Schema.Int.pipe(Schema.nonNegative());
export type DocVersion = typeof DocVersion.Type;

// The stored diff-match-patch text a doc-modifying event carries, stamped with the `docVersion` it
// produced. It lives here (not in `sync.ts`) so the audit schema is the leaf module: `sync.ts` builds
// the WS `Event` envelope around `AuditEvent`, and a leaf-ward `Patch` keeps that dependency acyclic.
// `core` types `patch` as an opaque string and never imports `diff-match-patch-ts`; the broadcast
// (apps/server) and client (apps/web) own `patch_make`/`patch_apply`.
export const Patch = Schema.TaggedStruct("patch", {
  version: DocVersion,
  patch: Schema.String,
});
export type Patch = typeof Patch.Type;

// Doc-modifying events carry the diff they applied and the version it produced, making the
// document replayable from origin (ADR-0006). `patch` reuses the live-sync wire type so the
// stored patch and the broadcast patch are one representation (P2.U4) — the seam sets its
// `version` equal to `docVersion`. Both optional: historical events predate the chain (this
// schema change is additive), and a write whose bytes are unchanged advances neither.
const docChange = {
  patch: Schema.optional(Patch),
  docVersion: Schema.optional(DocVersion),
};

// Who authored an annotation. Set once at creation and never changed — it is the
// permanent manual-vs-judge identity (CONTEXT.md): "Is this manual?" is
// `author === "expert"`, never inferred from the rule string. `expert` is a
// human-authored manual annotation; the two judge values are the automated passes.
export const Author = Schema.Literal("expert", "unit-judge", "cross-unit-judge");
export type Author = typeof Author.Type;

// The agent-safe subset of `Author`: the automated judge passes only. `expert` is the *sole*
// manual-identity authority (a manual finding bypasses Gate 1's classify routing, is a valid
// zero-byte reclassify target, and counts as human in the metrics), so it must never be assertable
// by an MCP caller. Agent-facing tool parameters carry this narrowed union; `expert` is reserved
// for the server-side REST/human edge (`rest.ts`), which calls the op directly with the full union.
export const JudgeAuthor = Schema.Literal("unit-judge", "cross-unit-judge");
export type JudgeAuthor = typeof JudgeAuthor.Type;

// The genesis event (ADR-0006): a document enters the patch chain with its full initial bytes
// recorded as the diff from the empty string, at `docVersion` 1. Every later patch folds forward
// over this origin, so the log alone reconstructs any version — the file becomes a pure
// materialization rather than the chain's only base. Seeded exactly once, when a pristine document
// (no review markup yet) is first observed; a document already carrying a chain is never re-seeded.
// Unlike the other doc-modifying events, `patch` and `docVersion` are **required** here (R6): a
// genesis event *is* the chain's origin, so one missing them would reconstruct silently to "" for
// version 1. There are no historical genesis events predating this (genesis was introduced with the
// patch chain), so requiring them is not a migration concern.
// `clonedFrom` is the optional clone-lineage pointer (ADR-0024): a clone stamps it with the source
// `DocumentId`; an import or a seed omits it, marking that document an *origin*. Lineage is a
// parent-pointer chain over independent logs — never a copy of the source's history. Optional so a
// seeded/imported genesis (which omits it) round-trips unchanged and `seedDocument` needs no change.
export const DocumentCreated = Schema.TaggedStruct("document.created", {
  documentId: DocumentId,
  patch: Patch,
  docVersion: DocVersion,
  clonedFrom: Schema.optional(DocumentId),
  at: Schema.DateTimeUtc,
});

export const AnnotationCreated = Schema.TaggedStruct("annotation.created", {
  annotationId: AnnotationId,
  documentId: DocumentId,
  // `author` was added with manual annotations; every `annotation.created` event predating it is, by
  // definition, a judge finding (the Expert could not yet author one). So a legacy log line without
  // `author` decodes to a judge value rather than failing the whole read — `unit-judge` is a safe
  // representative since nothing distinguishes the two judge authors (only `author === "expert"` is
  // ever tested). Encode always writes the field, so new events stay fully tagged on disk.
  author: Schema.optionalWith(Author, { default: () => "unit-judge" as const }),
  rule: Schema.String,
  rationale: Schema.String,
  span: Schema.String,
  // The content-hash version of the cited rule at write time (R3), the bucket key the
  // per-version trend reads. Optional so the whole JSONL still decodes: historical events
  // predate it (backfilled by the one-time P1.U4 migration), and a finding whose rule is not
  // in the catalog — an unknown citation or a `manual:*` sentinel — carries none.
  ruleVersion: Schema.optional(RuleContentHash),
  // How broad a problem the Expert marked this finding as (`selection` | `module` | `cycle` |
  // `unit-plan` | `level`). Set only by the manual authoring path; optional so every legacy
  // `annotation.created` line (and every judge finding, which carries none) still decodes. Encode
  // writes it only when present, so judge findings stay scope-free on disk.
  scope: Schema.optional(AnnotationScope),
  ...docChange,
  at: Schema.DateTimeUtc,
});

// A Gate-1 keep is zero-byte (ADR-0011): the `kept` status projects from this event, not from a
// token in the document, so the markup carries only the anchor id and the keep moves no bytes. Both
// `patch`/`docVersion` are therefore normally absent; they remain optional only so a decision that
// did happen to rewrite bytes (a legacy stamping keep, or future byte-moving accept) can still carry
// its change uniformly through `docChange`.
export const AnnotationAgreed = Schema.TaggedStruct("annotation.agreed", {
  annotationId: AnnotationId,
  documentId: DocumentId,
  reason: Schema.String,
  ...docChange,
  at: Schema.DateTimeUtc,
});

export const AnnotationDisagreed = Schema.TaggedStruct("annotation.disagreed", {
  annotationId: AnnotationId,
  documentId: DocumentId,
  reason: Schema.String,
  removedMarkup: Schema.String,
  ...docChange,
  at: Schema.DateTimeUtc,
});

// The Expert closes a finding without disputing it (a disagree) or regenerating from it (an
// agree): acknowledged and done — e.g. addressed by hand, or noted and not worth acting on. Same
// byte transition as a disagree (the carrier leaves the document), but a distinct event so the
// decision corpus's kept/rejected tallies — which feed per-rule trust — never count a closure as a
// verdict on the rule.
export const AnnotationClosed = Schema.TaggedStruct("annotation.closed", {
  annotationId: AnnotationId,
  documentId: DocumentId,
  reason: Schema.String,
  removedMarkup: Schema.String,
  ...docChange,
  at: Schema.DateTimeUtc,
});

// `resolves` is **0..N** (relaxed from NonEmptyArray, plan 2026-07-06 Unit 4 / R1): empty only for a
// span-less Expert direction (a chat instruction with no target span); a span-targeted direction
// anchors a manual Annotation first and this carries 1..N as before. Historical events with a
// non-empty `resolves` decode unchanged — the relaxation is a pure widening.
export const SuggestionCreated = Schema.TaggedStruct("suggestion.created", {
  suggestionId: SuggestionId,
  documentId: DocumentId,
  resolves: Schema.Array(AnnotationId),
  reason: Schema.String,
  operations: Schema.NonEmptyArray(EditOperation),
  ...docChange,
  at: Schema.DateTimeUtc,
});

export const SuggestionAccepted = Schema.TaggedStruct("suggestion.accepted", {
  suggestionId: SuggestionId,
  documentId: DocumentId,
  reason: Schema.String,
  priorText: Schema.String,
  ...docChange,
  at: Schema.DateTimeUtc,
});

export const SuggestionRejected = Schema.TaggedStruct("suggestion.rejected", {
  suggestionId: SuggestionId,
  documentId: DocumentId,
  reason: Schema.String,
  removedMarkup: Schema.String,
  ...docChange,
  at: Schema.DateTimeUtc,
});

// The agent's self-correction of its own PENDING suggestion (Q8, the `amend-suggestion` op): the old
// edit operations under `suggestionId` are stripped and replaced by `operations`, under the **same**
// id — `resolves` carries forward (preserved, unioned with any newly-folded collateral, never
// shrunk) so accept/reject still resolves exactly what it did before the amendment. Same shape as
// `SuggestionCreated` (including `docChange`) so the document replays from origin through the
// amendment: the strip-then-rewrite is one commit-seam diff, one event. `amend-suggestion` refuses
// (recoverable at the op boundary, not here) when the suggestion is no longer PENDING, so this event
// never appends for an already-decided suggestion.
export const SuggestionAmended = Schema.TaggedStruct("suggestion.amended", {
  suggestionId: SuggestionId,
  documentId: DocumentId,
  resolves: Schema.Array(AnnotationId),
  reason: Schema.String,
  operations: Schema.NonEmptyArray(EditOperation),
  ...docChange,
  at: Schema.DateTimeUtc,
});

export const TriageDisposed = Schema.TaggedStruct("triage.disposed", {
  annotationId: AnnotationId,
  documentId: DocumentId,
  disposition: Disposition,
  ...docChange,
  at: Schema.DateTimeUtc,
});

// Historical, no longer emitted (R1): the now-retired classify pass pinned a manual annotation to a
// rule (or marked it a rubric gap), overwriting the `manual:unclassified` sentinel `rule` while the
// permanent `author` stayed `expert`. `rule` was either a real rule citation or `manual:rule-gap`.
// The variant stays in the `AuditEvent` union — and `foldReview` still folds it to a terminal
// `"reclassified"` status — purely for reader tolerance: existing append-only audit logs can already
// contain this event from before the retirement, and those logs are immutable
// (`beam-workflows-tagged-data-immutability.md`), so every reader must keep replaying it correctly.
// No op in this workspace appends a new one.
export const AnnotationReclassified = Schema.TaggedStruct("annotation.reclassified", {
  annotationId: AnnotationId,
  documentId: DocumentId,
  rule: Schema.String,
  rationale: Schema.String,
  ...docChange,
  at: Schema.DateTimeUtc,
});

// The Expert's recorded retraction of an own manual annotation, valid any time it is still open —
// pending, not yet kept/rejected/closed (CONTEXT.md *Strip*; R1 widened this from "still unclassified"
// now that a manual annotation is an ordinary open finding, never classified). Append-only — never a
// deletion.
export const AnnotationStripped = Schema.TaggedStruct("annotation.stripped", {
  annotationId: AnnotationId,
  documentId: DocumentId,
  ...docChange,
  at: Schema.DateTimeUtc,
});

// A stale-finding retirement (R2, ADR-0015): a neutral terminal event recording that a pending judge
// finding was retired because its cited rule moved (`rule-revised`) or was removed (`rule-removed`)
// since the finding was stamped. Modelled on `AnnotationStripped` (no `removedMarkup` — that field is
// write-only for undo, which retirement does not yet support). `supersededVersion` is the finding's
// **own** obsoleted `ruleVersion` (the "was"), not the current catalog hash. Metric-neutral by
// construction: `projectDecisions` folds only agreed/disagreed, so this is ignored.
export const AnnotationRetired = Schema.TaggedStruct("annotation.retired", {
  annotationId: AnnotationId,
  documentId: DocumentId,
  reason: Schema.Literal("rule-revised", "rule-removed"),
  supersededVersion: Schema.optional(RuleContentHash),
  ...docChange,
  at: Schema.DateTimeUtc,
});

// Expert direct editing (propose → confirm). An `EditProposal` is the Expert's own change, distinct
// from an agent `Suggestion`: it carries `operations` and the `sweeps` (pending annotations a
// delete/substitute encloses) it will clear on confirm. `edit.proposed` adds the pending CriticMarkup
// (a byte-changing write, so it carries `patch`/`docVersion`); `edit.applied` collapses it to clean
// text; `edit.discarded` strips it, restoring the prior bytes (and leaving any swept findings live).
// Confirming an edit is the Expert endorsing each swept finding's rule — the `resolve` op appends a
// matching `annotation.agreed` per sweep, so the keep feeds per-rule trust through the existing fold.
export const EditProposed = Schema.TaggedStruct("edit.proposed", {
  editProposalId: EditProposalId,
  documentId: DocumentId,
  operations: Schema.NonEmptyArray(EditOperation),
  sweeps: Schema.Array(AnnotationId),
  reason: Schema.String,
  ...docChange,
  at: Schema.DateTimeUtc,
});

export const EditApplied = Schema.TaggedStruct("edit.applied", {
  editProposalId: EditProposalId,
  documentId: DocumentId,
  reason: Schema.String,
  priorText: Schema.String,
  ...docChange,
  at: Schema.DateTimeUtc,
});

export const EditDiscarded = Schema.TaggedStruct("edit.discarded", {
  editProposalId: EditProposalId,
  documentId: DocumentId,
  reason: Schema.String,
  removedMarkup: Schema.String,
  ...docChange,
  at: Schema.DateTimeUtc,
});

// A structural drag-reorder of a section from the outline (ADR-0033). Unlike an Expert edit-proposal,
// a move is applied *directly* — dragging is direct manipulation, so the section relocates immediately
// with no Gate-2 confirm. The event records which section moved and where (the `movedLine` heading, the
// target row, and the placement) for provenance; the actual relocation rides in `patch`/`docVersion`
// like every other doc-changing event, so the broadcast and the patch chain pick it up generically.
// `priorText` lets a future undo restore the pre-move bytes without replaying the whole chain (mirrors
// `edit.applied`).
export const SectionMoved = Schema.TaggedStruct("document.section-moved", {
  documentId: DocumentId,
  movedLine: Schema.Int.pipe(Schema.positive()),
  targetLine: Schema.Int.pipe(Schema.positive()),
  placement: Schema.Literal("before", "after", "first-child"),
  priorText: Schema.String,
  ...docChange,
  at: Schema.DateTimeUtc,
});

// A DMP-era user-facing undo or redo of the Expert's content edits (ADR-0036, RETIRED — nothing
// writes this event any more; the read-only History tab plus point-in-history clone replaced it).
// Retained so existing audit chains still decode: an unknown tag in a stored log kills the WS fold.
// Undo was a *forward* event on the append-only chain, never a rewrite — it reproduced a prior
// content version's bytes as a new `patch`/`docVersion`, so it broadcast and folded exactly like any
// other doc-changing event. `kind` distinguished undo from redo and `toVersion` records which content
// version's bytes were reproduced (provenance; the actual relocation rides in `patch`). `docChange`
// is present in practice (a real revert changes bytes) but optional for the rare byte-identical one.
export const DocumentReverted = Schema.TaggedStruct("document.reverted", {
  documentId: DocumentId,
  kind: Schema.Literal("undo", "redo"),
  toVersion: DocVersion,
  ...docChange,
  at: Schema.DateTimeUtc,
});

// LEGACY (pre identity/status split): the Expert archived a curriculum out of the working index
// (and could restore it) via these zero-byte events. Archived is now a value of the `status`
// lifecycle axis, set through `document.status-updated`; these two variants are NO LONGER EMITTED
// but remain in the union and are folded by `AuditLog.curriculumStatus` (archived → status
// "archived", unarchived → status cleared) so existing logs decode and project unchanged (ADR-0043
// COMPAT RULE). Expert/UI-only, like `document.section-moved` — never an agent capability.
export const DocumentArchived = Schema.TaggedStruct("document.archived", {
  documentId: DocumentId,
  // Always zero-byte in practice; `docChange` is spread for the union-wide uniform shape every
  // consumer relies on (`event.docVersion` is readable on any event).
  ...docChange,
  at: Schema.DateTimeUtc,
});
export const DocumentUnarchived = Schema.TaggedStruct("document.unarchived", {
  documentId: DocumentId,
  ...docChange,
  at: Schema.DateTimeUtc,
});

// The Expert updates a curriculum's IDENTITY tags — level + language (see `curriculum-tags.ts`). A
// document's INITIAL tags ride its genesis event (`DocumentCreatedCollab.tags`, below); this is the
// update event for every later change. Zero-byte: the current tag set is a projection over the
// LATEST tags-bearing event — genesis or update, whichever is newest (`AuditLog.curriculumTags`) —
// never a byte or store mutation, so retagging leaves the document's text and review history
// untouched. Expert/UI-only (curriculum page); never an agent capability (the agent's surface is
// the read-only `list-curricula` rows).
export const DocumentTagsUpdated = Schema.TaggedStruct("document.tags-updated", {
  documentId: DocumentId,
  tags: CurriculumTags,
  // Always zero-byte in practice; `docChange` is spread for the union-wide uniform shape every
  // consumer relies on (`event.docVersion` is readable on any event).
  ...docChange,
  at: Schema.DateTimeUtc,
});
export type DocumentTagsUpdated = typeof DocumentTagsUpdated.Type;

// The Expert moves a curriculum along its lifecycle `status` axis — exploratory / latest / stable /
// archived (see `curriculum-tags.ts`). The sibling of `document.tags-updated` for the OTHER axis:
// zero-byte, latest status-bearing event wins (`AuditLog.curriculumStatus`, which also folds the
// legacy `document.archived`/`unarchived` events). Setting `archived` is how a curriculum leaves the
// working rotation (the former Archive action). Expert/UI-only (curriculum page); the agent reads
// status through `list-curricula` but never writes it.
export const DocumentStatusUpdated = Schema.TaggedStruct("document.status-updated", {
  documentId: DocumentId,
  status: CurriculumStatus,
  // Always zero-byte in practice; `docChange` is spread for the union-wide uniform shape.
  ...docChange,
  at: Schema.DateTimeUtc,
});
export type DocumentStatusUpdated = typeof DocumentStatusUpdated.Type;

// ─── Role / phase lifecycle events (the status-axis split) ────────────────────
//
// These five replace the single `document.status-updated` above, which conflated *which* document
// this is (role) with *how ready* it is (phase). All zero-byte and latest-wins, exactly the
// ADR-0056 pattern the status event already used; `foldCurriculumLifecycle` in
// `curriculum-lifecycle.ts` is the one reader, and it also folds the legacy status/archive events so
// old logs project onto the new axes without rewriting a byte.

// The Expert changes which document is the one for its level. `supersedes` is stamped when a
// promotion replaces an incumbent canonical: it records SUCCESSION, deliberately separate from
// `clonedFrom` (which records where the bytes came from and never changes). A promoted experiment
// often has no clone relationship to the canonical it replaces, so the two cannot be conflated.
export const DocumentRoleUpdated = Schema.TaggedStruct("document.role-updated", {
  documentId: DocumentId,
  role: CurriculumRole,
  supersedes: Schema.optional(DocumentId),
  ...docChange,
  at: Schema.DateTimeUtc,
});
export type DocumentRoleUpdated = typeof DocumentRoleUpdated.Type;

// The Expert moves the document forward through production. Forward-only: the fold IGNORES an event
// that does not advance, so even a hand-edited log cannot make a document regress. Recorded, never
// gated — check-suite results are advisory and the Expert is authoritative.
export const DocumentPhaseAdvanced = Schema.TaggedStruct("document.phase-advanced", {
  documentId: DocumentId,
  phase: CurriculumPhase,
  ...docChange,
  at: Schema.DateTimeUtc,
});
export type DocumentPhaseAdvanced = typeof DocumentPhaseAdvanced.Type;

// Publishing PINS a version — the one learners see. Separate from `phase: published` (which says the
// content cleared both reviews) so head can keep moving while the pin holds: the pin changes only
// when an Expert publishes again, however many versions later.
export const DocumentPublished = Schema.TaggedStruct("document.published", {
  documentId: DocumentId,
  publishedVersion: DocVersion,
  ...docChange,
  at: Schema.DateTimeUtc,
});
export type DocumentPublished = typeof DocumentPublished.Type;

// A live document re-enters an earlier phase for continuous improvement. It does NOT leave
// `published` and the pin is untouched — learners keep seeing the pinned version while the work
// happens above it. `phase` names the phase re-entered.
export const DocumentReworkOpened = Schema.TaggedStruct("document.rework-opened", {
  documentId: DocumentId,
  phase: CurriculumPhase,
  ...docChange,
  at: Schema.DateTimeUtc,
});
export type DocumentReworkOpened = typeof DocumentReworkOpened.Type;

export const DocumentReworkClosed = Schema.TaggedStruct("document.rework-closed", {
  documentId: DocumentId,
  ...docChange,
  at: Schema.DateTimeUtc,
});
export type DocumentReworkClosed = typeof DocumentReworkClosed.Type;

// ─── Collab-era audit events (ADR-0043 / ADR-0044) ───────────────────────────
//
// COMPAT RULE: Do NOT delete or rename any event variant above — old JSONL/Postgres
// logs must still decode. These are additive extensions only. Legacy variants that
// carry DMP `patch`/`docVersion` remain decodable for migration (U15); the new
// variants carry `ChangeSetJson` + `newVersion` instead.
//
// Patch-bearing collab events advance `docVersion` (now `collabVersion === docVersion`
// by ADR-0043); non-patch events advance `seq` only (ADR-0011 dual-cursor stays).

// A draft was landed — the single commit that results from one explicit land through
// the commit seam (ADR-0043). PATCH-BEARING: advances docVersion.
// `gatedBy` is present whenever the LANDER differs from the draft's OWNER: an agent-owned
// draft accepted by a human via gate 2, or (U13 Part B) a human-owned draft confirmed/landed by
// a human other than its author. Absent for an ordinary self-land (owner confirms their own
// draft) — the audit trail only needs a second identity when one is actually distinct from the
// owner already on the event.
export const DraftLanded = Schema.TaggedStruct("draft.landed", {
  draftId: DraftId,
  documentId: DocumentId,
  owner: EditorIdentity,
  // Present when the lander is not the draft's owner (gate-2 accept, or a cross-owner human land).
  gatedBy: Schema.optional(EditorIdentity),
  // The final composite ChangeSet that landed (rebased against the head at land time).
  changeSetJson: ChangeSetJson,
  // Human-readable reason/context for the change.
  reason: Schema.optional(Schema.String),
  // The submitting author's mandatory one-line change description, carried through from the draft
  // (`Draft.description`, stamped at submit). Optional here so historical `draft.landed` events (and
  // any land of a pre-description migrated draft) still decode.
  description: Schema.optional(Schema.String),
  // The display name of the human who caused this agent-owned draft to be opened, carried through
  // from `Draft.instructedByDisplayName` (REST-triggered "Expand exercises" runs only — see its doc
  // comment on `Draft`). Absent for every other draft, and for any draft landed before this field
  // existed.
  instructedByDisplayName: Schema.optional(Schema.String),
  // The finding-id(s) this draft named as answered (ADR-0049 D3), carried through from
  // `Draft.resolves`. The land seam emits one `annotation.resolved.v2` per still-open id; this field
  // records what the draft claimed on the land event itself. `optionalWith default []` so historical
  // `draft.landed` events (which predate it) decode to an empty list.
  resolves: Schema.optionalWith(Schema.Array(AnnotationId), { default: () => [] }),
  // The H1 title of the post-land text, captured for free at the seam (`commitChangeSet` already
  // holds the full new text before building this event) so listing can read it without
  // reconstructing. `string` when the land's text has an H1, `null` when it confirmably does not
  // (`titleOf` found none), absent (`undefined`) only on events written before this field existed —
  // that tri-state is what lets a reader distinguish "known, no title" from "unknown, fall back".
  title: Schema.optional(Schema.NullOr(Schema.String)),
  // The new collabVersion this commit produced (collabVersion === docVersion per ADR-0043).
  newVersion: CollabVersion,
  // `docVersion` mirrors `newVersion` for backward compat with code that reads
  // `event.docVersion` uniformly across the AuditEvent union (e.g. jsonl-audit-log,
  // headVersion fold). The collab model defines `collabVersion === docVersion`, so both
  // fields carry the same value — newVersion is the canonical field; docVersion is the
  // compat alias consumed by existing infrastructure until U3 fully migrates it.
  docVersion: Schema.optional(DocVersion),
  // Added/removed LINE counts stamped at land from the true (pre-image, post-image) text pair (U1).
  // Optional because every event appended before CON-1175 predates them: ABSENT MEANS UNKNOWN and is
  // never defaulted to 0 — a genuinely no-op land must stay distinguishable from an unstamped row, or
  // the History chip silently lies. Read through `changeLineCounts` below, never field-by-field.
  addedLines: Schema.optional(Schema.Int.pipe(Schema.nonNegative())),
  removedLines: Schema.optional(Schema.Int.pipe(Schema.nonNegative())),
  at: Schema.DateTimeUtc,
});
export type DraftLanded = typeof DraftLanded.Type;

// A document was created in the collab era — the initial text is encoded as a
// ChangeSet insert from the empty string (ChangeSet.fromJSON(changeSetJson).apply(Text.empty)
// yields the full initial bytes). PATCH-BEARING: always produces newVersion=1.
// `docVersion` mirrors `newVersion` as a compat alias (same semantics as DraftLanded).
// `clonedFrom` preserves the clone-lineage pointer from the legacy variant (ADR-0024).
// NOTE: the tag is "document.created.v2" to avoid ambiguity with the legacy DMP-era
// "document.created" event in the AuditEvent union (both must remain decodable, ADR-0043).
export const DocumentCreatedCollab = Schema.TaggedStruct("document.created.v2", {
  documentId: DocumentId,
  // The full initial document as a ChangeSet insert from the empty origin.
  changeSetJson: ChangeSetJson,
  // The collabVersion produced (always 1 for a fresh document genesis).
  newVersion: CollabVersion,
  // compat alias for legacy folds that read event.docVersion uniformly.
  docVersion: Schema.optional(DocVersion),
  // Optional clone-lineage pointer (ADR-0024): present when this document is a clone.
  clonedFrom: Schema.optional(DocumentId),
  // The source version this document was cloned from (versioned-clone lineage): the head version
  // for a plain Clone, or the chosen historical version for a Clone-from-history. Follows the
  // additive `clonedFrom`/`tags`/`status` precedent — no `document.created.v3` tag — so every
  // existing `document.created.v2` event (with no `clonedFromVersion`) decodes unchanged (R6). The
  // stamped version and the cloned bytes derive from a single read, so they can never disagree (R8).
  clonedFromVersion: Schema.optional(CollabVersion),
  // The document's INITIAL identity tags (level + language; the curriculum-tags ADR): stamped at
  // Import (required on the wire) and at Clone (source tags inherited), making creation + tagging one
  // atomic append. Optional so seeded documents and pre-tags genesis events decode unchanged
  // (untagged). Later changes append `document.tags-updated`; the projection takes the latest.
  tags: Schema.optional(CurriculumTags),
  // The document's INITIAL lifecycle status: stamped at Import (`latest` by default) and at Clone
  // (`exploratory` — a fresh working copy). Optional so pre-split genesis events decode unchanged
  // (no status). Later changes append `document.status-updated`; the lifecycle fold takes the latest.
  status: Schema.optional(CurriculumStatus),
  // The document's INITIAL role and phase (the status-axis split), so an import declares which
  // document this is and where it starts in production as part of the same atomic append. Optional
  // on the same additive precedent as `tags`/`status`: every genesis written before this change
  // decodes unchanged and simply falls to the fold's defaults (canonical / structure).
  role: Schema.optional(CurriculumRole),
  phase: Schema.optional(CurriculumPhase),
  // The H1 title of the initial text, same tri-state semantics as `DraftLanded.title` above:
  // `string`, or `null` when the imported/cloned text confirmably has no H1, absent on events
  // written before this field existed.
  title: Schema.optional(Schema.NullOr(Schema.String)),
  // Added/removed LINE counts stamped at land (U1/CON-1175) — see `DraftLanded.addedLines` above for
  // the absent-means-unknown rationale. Genesis always has `removedLines === 0` (the pre-image is "").
  addedLines: Schema.optional(Schema.Int.pipe(Schema.nonNegative())),
  removedLines: Schema.optional(Schema.Int.pipe(Schema.nonNegative())),
  // Set when this genesis was minted to seed an async generation run rather than authored content
  // (KTD1): the document exists so viewers can open/watch it, but its head text is the empty
  // insert until the run's terminal event (`document.generation-completed` /
  // `document.generation-failed`, below) lands. Absent for every ordinary genesis (import, clone,
  // seed) — every existing `document.created.v2` line decodes unchanged.
  origin: Schema.optional(Schema.Literal("generating")),
  at: Schema.DateTimeUtc,
}).pipe(
  // A `generating`-origin genesis is a placeholder: its `changeSetJson` must be the canonical
  // empty-insert changeset (`[]`, verified empirically against `@codemirror/state`'s `ChangeSet`)
  // so there is no real content to lose track of while the run is in flight. Every non-generating
  // genesis is unconstrained, so this is additive over every existing decodable line.
  Schema.filter((event) =>
    event.origin === "generating" && event.changeSetJson.length !== 0
      ? "a generating-origin genesis must carry the canonical empty-insert changeset ([])"
      : true,
  ),
);
export type DocumentCreatedCollab = typeof DocumentCreatedCollab.Type;

// The lifecycle/tags/title-projection view of a genesis event: every field EXCEPT the full-document
// `changeSetJson` bytes, which dwarf everything else on the row (avg 241 KB across a real corpus,
// vs. under 200 bytes for the rest of the event) and are never read by those projections — only
// role/phase/status/tags/title/at are. Derived via `Schema.omit`, not hand-duplicated, so a field
// added to `DocumentCreatedCollab` appears here automatically and the two shapes cannot drift. A
// read path that only needs those scalars decodes through this instead of the full event, so
// Postgres never ships the content bytes and the app never parses them. `Schema.omit` operates on
// the underlying struct's fields, so it drops the `changeSetJson`-shape filter above along with the
// field it constrains — correct here since Lite never has the bytes to check the invariant against.
export const DocumentCreatedCollabLite = DocumentCreatedCollab.pipe(Schema.omit("changeSetJson"));
export type DocumentCreatedCollabLite = typeof DocumentCreatedCollabLite.Type;

// A direct land with no draft — a single commit through the seam, without going through the draft
// lifecycle. PATCH-BEARING: advances docVersion.
// Both original producers are now retired (the Expert section move in U9/ADR-0078, the Expert
// revert with the rest of the undo/redo surface), so no production caller emits this today; the
// seam and this event are retained because stored chains carry both and still have to decode.
// `reason` is optional provenance (e.g. "revert to v3", "move section").
// `docVersion` mirrors `newVersion` as a compat alias (same as DraftLanded).
export const CommitLanded = Schema.TaggedStruct("commit.landed", {
  documentId: DocumentId,
  // Who performed the direct land (Expert viewer or Expert agent identity).
  owner: EditorIdentity,
  // The ChangeSet applied to the head text at land time.
  changeSetJson: ChangeSetJson,
  // The new collabVersion this commit produced.
  newVersion: CollabVersion,
  // compat alias for legacy folds that read event.docVersion uniformly.
  docVersion: Schema.optional(DocVersion),
  // Human-readable reason for the direct land (revert annotation, move description, etc.).
  reason: Schema.optional(Schema.String),
  // Added/removed LINE counts stamped at land (U1/CON-1175) — see `DraftLanded.addedLines` above for
  // the absent-means-unknown rationale. Widened for uniformity; no production caller writes this event.
  addedLines: Schema.optional(Schema.Int.pipe(Schema.nonNegative())),
  removedLines: Schema.optional(Schema.Int.pipe(Schema.nonNegative())),
  at: Schema.DateTimeUtc,
});
export type CommitLanded = typeof CommitLanded.Type;

// The two terminal events a `generating`-origin genesis (KTD1) resolves to — never both, and a
// document may have at most one of either following its generating genesis:
//   - "document.generation-completed" — the run produced real content. PATCH-BEARING: the
//     ChangeSet that replaces the empty genesis text, same shape as `document.created.v2`'s own
//     initial-content ChangeSet. `docVersion` mirrors `newVersion` as a compat alias (same
//     convention as `CommitLanded`/`DraftLanded`), so `chainFactsOf`'s generic `docVersion` probe
//     advances the same way it does for any other patch-bearing event.
//   - "document.generation-failed" — the run failed. Zero-byte: the document stays exactly as its
//     empty genesis left it, and `reason` is a human-readable failure summary (not a tagged error —
//     this crosses into the audit log, which is data, not a live error channel).
export const DocumentGenerationCompleted = Schema.TaggedStruct("document.generation-completed", {
  documentId: DocumentId,
  changeSetJson: ChangeSetJson,
  newVersion: CollabVersion,
  docVersion: Schema.optional(DocVersion),
  // Stamped at land from the true (pre-image, post-image) text pair, same convention as every other
  // patch-bearing event (U1/CON-1175) — the History tab's diff-size chip reads these, never the
  // ChangeSet itself. Pre-image is always the empty placeholder text, so added = every line of the
  // generated content, removed = 0.
  addedLines: Schema.optional(Schema.Int.pipe(Schema.nonNegative())),
  removedLines: Schema.optional(Schema.Int.pipe(Schema.nonNegative())),
  at: Schema.DateTimeUtc,
});
export type DocumentGenerationCompleted = typeof DocumentGenerationCompleted.Type;

// The Lite twin (mirrors `DocumentCreatedCollabLite`): every field except the content bytes, for a
// store that projects `LifecycleEvent`'s fields without ever reading `changeSetJson`.
export const DocumentGenerationCompletedLite = DocumentGenerationCompleted.pipe(
  Schema.omit("changeSetJson"),
);
export type DocumentGenerationCompletedLite = typeof DocumentGenerationCompletedLite.Type;

export const DocumentGenerationFailed = Schema.TaggedStruct("document.generation-failed", {
  documentId: DocumentId,
  reason: Schema.String,
  // Zero-byte (never actually set): carried purely for uniform `event.docVersion` access across
  // the AuditEvent union (jsonl-audit-log.ts, headVersion fold), same convention as every other event.
  docVersion: Schema.optional(DocVersion),
  at: Schema.DateTimeUtc,
});
export type DocumentGenerationFailed = typeof DocumentGenerationFailed.Type;

// A human rejected a submitted agent draft — the draft is discarded, the doc untouched.
// NOT patch-bearing: advances seq only. Carries docVersion (optional) for uniform
// event.docVersion access across the AuditEvent union. `changeSetJson` records the diff the
// rejected draft WOULD have applied (informational only, so a rejection is as analysable as a
// land) — it carries bytes, but it is NEVER applied to the document: this event stays
// non-patch-bearing / never applies to the document because `isPatchBearingCollabEvent`
// discriminates on `_tag` (`audit.ts:591`), never on the presence of `changeSetJson`, and
// `draft.landed` already proves a `changeSetJson` alone is not the applying signal. `patch` is
// intentionally absent to make "a rejection moved the document" unrepresentable at the schema level.
export const DraftRejected = Schema.TaggedStruct("draft.rejected", {
  draftId: DraftId,
  documentId: DocumentId,
  owner: EditorIdentity,
  decidedBy: EditorIdentity,
  reason: Schema.String,
  // The composite ChangeSet the rejected draft carried at reject time. OPTIONAL so historical
  // `draft.rejected` events (which predate this field) still decode — its absence is meaningful
  // ("unknown"), NOT "empty"; read it through `rejectedChangeSet` so the three-state distinction is
  // typed at the boundary and analyzers never coerce absent → an empty ChangeSet.
  changeSetJson: Schema.optional(ChangeSetJson),
  docVersion: Schema.optional(DocVersion),
  at: Schema.DateTimeUtc,
});
export type DraftRejected = typeof DraftRejected.Type;

// The rejected draft's recorded ChangeSet as an Option: `Some` when the event carries one, `None`
// for a legacy event predating the field. The `None` case is "unknown", not "empty" — a caller
// analysing rejected diffs must treat the two distinctly (never fold absent into an empty ChangeSet).
export const rejectedChangeSet = (event: DraftRejected): Option.Option<ChangeSetJson> =>
  event.changeSetJson === undefined ? Option.none() : Option.some(event.changeSetJson);

// A draft was discarded — either by its owner (store-only, no audit event needed for
// self-discard) or by any human reviewer (D11 cross-owner discard — this audit event
// records attribution). NOT patch-bearing. Carries docVersion but never patch —
// same non-patch-bearing reasoning as DraftRejected above (a discard never moves the document).
export const DraftDiscarded = Schema.TaggedStruct("draft.discarded", {
  draftId: DraftId,
  documentId: DocumentId,
  // The draft's owner (may differ from discardedBy on a cross-owner discard).
  owner: EditorIdentity,
  // Who performed the discard (may equal owner for self-discard when audited).
  discardedBy: EditorIdentity,
  reason: Schema.optional(Schema.String),
  docVersion: Schema.optional(DocVersion),
  at: Schema.DateTimeUtc,
});
export type DraftDiscarded = typeof DraftDiscarded.Type;

// A structured anchor — stored as head-relative offsets at the version they were
// resolved against (ADR-0043 / D12). Offsets are stored output, never op input.
export const StructuredAnchor = Schema.Struct({
  from: Schema.Int.pipe(Schema.nonNegative()),
  to: Schema.Int.pipe(Schema.nonNegative()),
  // The collabVersion at which these offsets were resolved (for forward mapping in U6).
  atVersion: CollabVersion,
});
export type StructuredAnchor = typeof StructuredAnchor.Type;

// NEW annotation-created event (collab era) — carries a structured anchor rather than a
// text `span`. The old markup-shaped `annotation.created` above MUST remain decodable
// for migration (U15 replays old logs); this is a separate _tag to avoid decode ambiguity.
// NOT patch-bearing. Carries docVersion (optional) for uniform event.docVersion access, but
// intentionally omits `patch`: collab-era decisions are zero-byte by design (ADR-0043), so
// allowing `patch` here would make an illegal state representable at the schema level.
export const AnnotationCreatedStructured = Schema.TaggedStruct("annotation.created.v2", {
  annotationId: AnnotationId,
  documentId: DocumentId,
  // The resolved head-relative anchor (D12).
  anchor: StructuredAnchor,
  rule: Schema.String,
  rationale: Schema.String,
  // Author field mirrors the legacy variant (same semantics).
  author: Schema.optionalWith(Author, { default: () => "unit-judge" as const }),
  // The PERSON who authored this finding (ADR-0051): the `ViewerId` of the Expert whose manual
  // finding this is, stamped only on the manual REST path (`rest.ts` annotateStructured). Judge- and
  // agent-authored findings carry a *role* `author` but no person, so this is `optional`: absent means
  // "no person" (every judge finding, every legacy manual finding written before this field). It is
  // server-side identity — read only by the open-findings projection to derive a display-name label,
  // and NEVER carried on a wire (ADR-0013): it is not on `AnnotationReviewState`/`ReviewStateWire`, and
  // the `read-findings` op derives it to `authorName` at its boundary. `expert`-role findings may still
  // omit it (legacy), so `author === "expert"` stays the manual-vs-judge authority, not `createdBy`.
  createdBy: Schema.optional(ViewerId),
  // The stamped real display name for the person in `createdBy` (mirrors
  // `ConversationStarted.ownerDisplayName`, ADR-0050 Decision 9): captured at write time from
  // `CurrentViewer.displayName` so a later identity-adapter swap or pseudonym re-derivation can never
  // change a finding's label after the fact. Optional: absent for every judge/agent finding and for a
  // manual finding written before this field existed — `projectDiagnostics` falls back to
  // `deriveDisplayName(createdBy)` in that case.
  createdByDisplayName: Schema.optional(Schema.String),
  ruleVersion: Schema.optional(RuleContentHash),
  scope: Schema.optional(AnnotationScope),
  // CON-734 (U3): which `Check` produced this finding — `${ruleId}@${partId}` (`check-suite.ts`
  // `buildSuite`), the `ruleId` already canonical (`canonicalizeRuleId`). Stamped only by the
  // check-suite write path (`run-checks.ts`); absent on a manual finding (REST) and on every
  // pre-U3 judge finding. `Schema.optional`, never defaulted — a default would make "not produced
  // by the check suite" indistinguishable from "produced before this field existed", and the two
  // are a real distinction U7 depends on. Additive, not a new `_tag`: same precedent as
  // `lint` on `DiagnosticsPayload` (`collab.ts`) — a frame predating this field still decodes.
  checkId: Schema.optional(Schema.String),
  // The `ContentPart` the check ran against: `partIdOf(headingPath)` for a unit-plan part, or
  // `OUTLINE_PART_ID` for the cross-unit outline part (`check-suite.ts`). Same optional/never-
  // defaulted reasoning as `checkId` above — the two are always stamped together.
  partId: Schema.optional(Schema.String),
  // CON-737 (U6): a manual finding's severity, stamped only by the manual REST write path
  // (`rest.ts` annotateStructured), when a severity picker supplies one. Judge and lint findings
  // never stamp this — their severity is DERIVED live from the rubric catalog / lint's own
  // `Severity` at the projection boundary (`review-state.ts`'s `deriveSeverity`,
  // `finding-summary.ts`), so re-tiering a rule re-buckets them without touching the log. A manual
  // finding has no catalog entry to derive from, so it is the one kind that stamps. `Schema.optional`,
  // never defaulted — same reasoning as `checkId`/`partId` above: a default would make "no severity
  // supplied" indistinguishable from "predates this field", and `deriveSeverity` needs to tell the
  // two apart to fall back correctly for a pre-U6 manual finding.
  severity: Schema.optional(FindingSeverity),
  docVersion: Schema.optional(DocVersion),
  at: Schema.DateTimeUtc,
});
export type AnnotationCreatedStructured = typeof AnnotationCreatedStructured.Type;

// NEW gate-1 decision events carrying annotation ids only (no offsets / markup in bytes).
// These REPLACE the byte-carrying forms as the canonical new shape; the legacy forms
// above remain decodable for migration. Zero-byte: NOT patch-bearing.
// Each carries docVersion (optional) for uniform event.docVersion access across the
// AuditEvent union, but intentionally omits `patch` — same zero-byte reasoning as above.

// Structured agree — ids only, no removedMarkup (ADR-0043 zero-byte decisions).
export const AnnotationAgreedV2 = Schema.TaggedStruct("annotation.agreed.v2", {
  annotationId: AnnotationId,
  documentId: DocumentId,
  reason: Schema.String,
  docVersion: Schema.optional(DocVersion),
  at: Schema.DateTimeUtc,
});
export type AnnotationAgreedV2 = typeof AnnotationAgreedV2.Type;

export const AnnotationDisagreedV2 = Schema.TaggedStruct("annotation.disagreed.v2", {
  annotationId: AnnotationId,
  documentId: DocumentId,
  reason: Schema.String,
  docVersion: Schema.optional(DocVersion),
  at: Schema.DateTimeUtc,
});
export type AnnotationDisagreedV2 = typeof AnnotationDisagreedV2.Type;

export const AnnotationClosedV2 = Schema.TaggedStruct("annotation.closed.v2", {
  annotationId: AnnotationId,
  documentId: DocumentId,
  reason: Schema.String,
  docVersion: Schema.optional(DocVersion),
  at: Schema.DateTimeUtc,
});
export type AnnotationClosedV2 = typeof AnnotationClosedV2.Type;

// Retract is also zero-byte in the new model.
export const AnnotationStrippedV2 = Schema.TaggedStruct("annotation.stripped.v2", {
  annotationId: AnnotationId,
  documentId: DocumentId,
  docVersion: Schema.optional(DocVersion),
  at: Schema.DateTimeUtc,
});
export type AnnotationStrippedV2 = typeof AnnotationStrippedV2.Type;

// ─── Annotation-affordance terminals (ADR-0049 / Plan 004) ───────────────────
//
// The accept-on-create lifecycle (ADR-0049) replaces the agree/disagree/close decision
// family with three additive terminal events. All are ZERO-BYTE / NOT patch-bearing —
// a finding's lifecycle never moves document bytes (ADR-0043), so `patch` is omitted to
// make "an annotation terminal moved the document" unrepresentable at the schema level.
// The agree/disagree/close(.v2) variants above stay decodable + folded (D2): an old log
// still replays them to the same terminal live-state; new code simply stops constructing
// them.

// The single user affordance (D1): the Expert (or, via the server edge, an agent-confirmed
// path) ignores a finding — "this is not a real issue / do not flag again". Terminal.
// `reason` is the free-text why (may be empty — ignoring is low-ceremony, matching the
// legacy close). The false-positive suppression fingerprint (D4a) is NOT denormalized onto
// this event: it is derived at judge-suppression time from the finding's own
// `annotation.created.v2` anchor (rule + forward-mapped region), which is append-only and
// always present in the same log — storing a copy here would risk drift with no benefit.
export const AnnotationIgnoredV2 = Schema.TaggedStruct("annotation.ignored.v2", {
  annotationId: AnnotationId,
  documentId: DocumentId,
  reason: Schema.String,
  docVersion: Schema.optional(DocVersion),
  at: Schema.DateTimeUtc,
});
export type AnnotationIgnoredV2 = typeof AnnotationIgnoredV2.Type;

// Automatic resolution (D3/D3a): a landing draft resolves the finding(s) it explicitly
// NAMES as answered (never by anchor-overlap). The land seam emits one of these per named
// finding-id, carrying the originating `draftId` so the resolution is auditable back to the
// draft that answered it. Terminal.
export const AnnotationResolvedV2 = Schema.TaggedStruct("annotation.resolved.v2", {
  annotationId: AnnotationId,
  documentId: DocumentId,
  // The draft whose land resolved this finding (provenance; D3a).
  draftId: DraftId,
  docVersion: Schema.optional(DocVersion),
  at: Schema.DateTimeUtc,
});
export type AnnotationResolvedV2 = typeof AnnotationResolvedV2.Type;

// Judge content-drift retraction (D4): a post-land judge re-run that no longer finds a
// previously-flagged issue retracts it. Restricted at the op layer to JUDGE-authored findings
// (an LLM re-run never authored a manual/agent-confirmed finding, so it must never retract
// one). Terminal. Distinct from `annotation.retired` (v1 rule-drift terminal, still emitted by
// `reconcile-stale-findings` unchanged) — retraction is the LLM-fresh-output withdrawal, a
// separate additive terminal.
export const AnnotationRetractedV2 = Schema.TaggedStruct("annotation.retracted.v2", {
  annotationId: AnnotationId,
  documentId: DocumentId,
  docVersion: Schema.optional(DocVersion),
  at: Schema.DateTimeUtc,
});
export type AnnotationRetractedV2 = typeof AnnotationRetractedV2.Type;

// An Expert override of a judge finding (U4, judge-finding identity/severity/stability plan):
// appended ALONGSIDE `annotation.ignored.v2` (never instead of it — the ignore event is still what
// makes `checkSingleTerminalDecision`/`terminalTarget` treat the annotation as terminal), whenever
// the ignored annotation was judge-authored. This is the false-positive corpus's raw material: the
// pure projection `falsePositiveExamples` (false-positive-examples.ts) folds these into
// `NegativeExample`s that `run-checks.ts` feeds back to the checker as trusted reference material,
// so a rule stops repeating a mistake the Expert has already corrected once. `ruleId` is the
// canonical bare id — `canonicalizeRuleId`'d before the branded `RuleId` is minted, asserted at
// write (see `resolve.ts`); the brand itself carries no format refinement, so a canonically-scoped
// reader (`falsePositiveExamples`) still re-canonicalizes on read as defence in depth against a
// future write-site bug, exactly as `judgeReconcileSets` treats `rule` elsewhere. `quotedSpan` is
// the exact text the judge flagged (read via `DocumentReadModel.at` at the annotation's anchor
// version, NOT the live document — the annotation may have been superseded since). `judgeReasoning`
// is the judge's own stated rationale (`annotation.created.v2.rationale`); `expertReason` is why the
// Expert says it is wrong (the same free-text `reason` already carried by `annotation.ignored.v2`).
//
export const AnnotationFalsePositive = Schema.TaggedStruct("annotation.falsePositive.v1", {
  annotationId: AnnotationId,
  documentId: DocumentId,
  ruleId: RuleId,
  // The `Check` whose verdict the Expert is overruling, copied verbatim off the ignored
  // annotation's own `annotation.created.v2.checkId` (U3, CON-734). Provenance only: nothing on the
  // corpus read path keys on it — `falsePositiveExamples` folds by `ruleId`, because an example
  // generalises across the parts a rule runs on and a `${ruleId}@${partId}` key would scope it to
  // one heading. It is here so a per-check false-positive rate is countable from the log alone,
  // which is what U7's flip-rate work needs. Optional and never defaulted, matching U3's own
  // modelling: absent means the ignored finding carried no `checkId` — a manual-path judge finding,
  // or any judge finding written before CON-734. A bare string, not a branded `CheckId`: no such
  // brand exists anywhere in this codebase, and inventing one here would put the only definition
  // downstream of the two places that already mint the value as a plain string.
  checkId: Schema.optional(Schema.String),
  quotedSpan: Schema.String,
  judgeReasoning: Schema.String,
  expertReason: Schema.String,
  // NOT patch-bearing (zero-byte, like `annotation.ignored.v2`); optional for uniform
  // `event.docVersion` access across the AuditEvent union (jsonl-audit-log.ts, headVersion fold).
  docVersion: Schema.optional(DocVersion),
  at: Schema.DateTimeUtc,
});
export type AnnotationFalsePositive = typeof AnnotationFalsePositive.Type;

// A one-time marker recorded by the U15 migration script (ADR-0043/ADR-0044 D8). It is the FIRST
// event appended after a migrated document's fresh `document.created.v2` genesis, naming where the
// document's pre-migration legacy log was archived (the full DMP-era history, preserved byte-for-
// byte — "archive = move, never delete"). Zero-byte: NOT patch-bearing, never carries `patch`. Its
// presence anywhere in a document's log is the migration's own idempotency signal — a document
// whose log already carries one is skipped on a re-run.
export const MigratedFrom = Schema.TaggedStruct("migrated-from", {
  documentId: DocumentId,
  // The archived legacy log's location, relative to the audit log's base directory.
  archivePath: Schema.String,
  docVersion: Schema.optional(DocVersion),
  at: Schema.DateTimeUtc,
});
export type MigratedFrom = typeof MigratedFrom.Type;

export const AuditEvent = Schema.Union(
  // ── Legacy events (decodable for migration U15) ──────────────────────────────
  /** legacy — decodable for migration only; new code produces DraftLanded instead */
  DocumentCreated,
  /** legacy — decodable for migration only; new code produces AnnotationCreatedStructured */
  AnnotationCreated,
  /** legacy — decodable for migration only; new code produces AnnotationAgreedV2 */
  AnnotationAgreed,
  /** legacy — decodable for migration only; new code produces AnnotationDisagreedV2 */
  AnnotationDisagreed,
  /** legacy — decodable for migration only; new code produces AnnotationClosedV2 */
  AnnotationClosed,
  AnnotationReclassified,
  /** legacy — decodable for migration only; new code produces AnnotationStrippedV2 */
  AnnotationStripped,
  AnnotationRetired,
  /** legacy — decodable for migration only; drafts replace suggestion model */
  SuggestionCreated,
  /** legacy — decodable for migration only */
  SuggestionAccepted,
  /** legacy — decodable for migration only */
  SuggestionRejected,
  SuggestionAmended,
  TriageDisposed,
  /** legacy — decodable for migration only; propose-edit retired in D9 */
  EditProposed,
  /** legacy — decodable for migration only */
  EditApplied,
  /** legacy — decodable for migration only */
  EditDiscarded,
  SectionMoved,
  DocumentReverted,
  DocumentArchived,
  DocumentUnarchived,
  DocumentTagsUpdated,
  /** legacy — decodable for old logs; the axis split into role + phase */
  DocumentStatusUpdated,
  // ── Role / phase lifecycle (the status-axis split) ───────────────────────────
  DocumentRoleUpdated,
  DocumentPhaseAdvanced,
  DocumentPublished,
  DocumentReworkOpened,
  DocumentReworkClosed,
  // ── Collab-era events (ADR-0043 / ADR-0044) ─────────────────────────────────
  DraftLanded,
  DraftRejected,
  DraftDiscarded,
  DocumentCreatedCollab,
  CommitLanded,
  DocumentGenerationCompleted,
  DocumentGenerationFailed,
  AnnotationCreatedStructured,
  AnnotationAgreedV2,
  AnnotationDisagreedV2,
  AnnotationClosedV2,
  AnnotationStrippedV2,
  // ── Annotation-affordance terminals (ADR-0049) ──────────────────────────────
  AnnotationIgnoredV2,
  AnnotationResolvedV2,
  AnnotationRetractedV2,
  AnnotationFalsePositive,
  MigratedFrom,
);
export type AuditEvent = typeof AuditEvent.Type;

// The stamped line counts as an Option, mirroring `rejectedChangeSet` above: `Some` only when BOTH
// `addedLines`/`removedLines` are present (a half-stamped event is unknown, not partial), `None` for
// every other event — including one whose variant never carries the fields at all.
export const changeLineCounts = (event: AuditEvent): Option.Option<LineCounts> =>
  "addedLines" in event &&
  "removedLines" in event &&
  event.addedLines !== undefined &&
  event.removedLines !== undefined
    ? Option.some({ addedLines: event.addedLines, removedLines: event.removedLines })
    : Option.none();

// ─── Patch-bearing event predicate ────────────────────────────────────────────
//
// A patch-bearing collab event advances docVersion (collabVersion) in the audit fold.
// Three event tags are now patch-bearing in the collab era (U3):
//   - "draft.landed"          — a user/agent draft landed through the commit seam
//   - "document.created.v2"   — a document was created (initial text as ChangeSet insert)
//   - "commit.landed"         — a direct land with no draft (historically a revert or section move)
//
// The legacy DMP-era events (DocumentCreated, AnnotationCreated, SectionMoved,
// DocumentReverted, etc.) carried `patch`/`docVersion` and are handled by doc-history.ts's
// legacy leg. They are NOT collab-era patch-bearing events.
//
// The annotation-affordance terminals (`annotation.ignored.v2`, `annotation.resolved.v2`,
// `annotation.retracted.v2`, ADR-0049) are ZERO-BYTE like every other annotation event, so
// they are deliberately NOT listed here — a finding lifecycle transition never advances
// docVersion.
//
// projectUpdateLog and the reconstruction fold both use this predicate to include all
// three new event kinds in the collab update feed.
// Exported so a store that can push work to its query engine selects exactly these rows rather than
// re-deriving the list (the same reason `LIFECYCLE_EVENT_TAGS` is exported).
export const PATCH_BEARING_COLLAB_TAGS = [
  "draft.landed",
  "document.created.v2",
  "commit.landed",
  "document.generation-completed",
] as const;

export const isPatchBearingCollabEvent = (
  e: AuditEvent,
): e is DraftLanded | DocumentCreatedCollab | CommitLanded | DocumentGenerationCompleted =>
  (PATCH_BEARING_COLLAB_TAGS as readonly string[]).includes(e._tag);

// Did this event move the document's BYTES? Both legs of the hybrid log: the collab-era patch
// carriers above, plus any legacy DMP event whose optional `patch` is actually set. `docChange` is
// spread across nearly the whole union for a uniform shape, so presence of the FIELD proves nothing —
// only a set `patch` does, which is why this cannot discriminate on `_tag` alone.
//
// This is exactly the set `patchRange` folds to reconstruct the text, so "what the text is" and "when
// the text last changed" are read off the same events. Zero-byte events (tags edited, phase advanced,
// published, annotations, a draft rejected) are deliberately excluded: they are activity on the
// document, not a change to it.
export const isDocumentModifying = (e: AuditEvent): boolean =>
  isPatchBearingCollabEvent(e) || ("patch" in e && e.patch !== undefined);

// The pure title fold: one document's ordered audit events in, its projected H1 title out. The
// invariant is "the H1 of the head text", so this reads `title` off the LATEST byte-moving event
// (`isDocumentModifying` above — the same predicate `curriculumLifecycle()`'s `updatedAt` folds
// against), not off the latest event that merely happens to carry a `title` key. That distinction
// matters: `CommitLanded` is a byte-mover with no `title` field (no live production caller stamps
// it, but old chains still decode it), and any other future patch-bearing event this module doesn't
// stamp would be the same shape. If the latest byte-mover has no `title` key, the result is
// `undefined` — "unknown, fall back to reconstruction" — never a stale title from an earlier event.
// `null` means the latest byte-mover's text was checked and confirmably has no H1.
export const foldCurriculumTitle = (events: readonly AuditEvent[]): string | null | undefined => {
  let title: string | null | undefined;
  for (const event of events) {
    if (!isDocumentModifying(event)) continue;
    title = "title" in event ? event.title : undefined;
  }
  return title;
};
